import i18n from '@/utils/i18n';
import { Chip } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  HorizontalScrollWithArrows,
  HorizontalScrollWithArrowsHandle,
} from '@/experiences/sales-portal/components/horizontal-scroll-with-arrows';
import { OrderGuideNewGroupChip } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-new-group-chip';
import { OrderGuidePartition } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { useCallback, useRef } from 'react';

interface GroupSelectorProps {
  /** When provided (with `validateGroupName`), renders the New-group chip after the pills. */
  onCreateGroup?: (name: string) => void;
  onSelectPartition: (partitionUUID: string) => void;
  partitions: OrderGuidePartition[];
  selectedPartitionUUID: string | null;
  /** Name validation for the New-group chip. */
  validateGroupName?: (name: string) => boolean;
}

export const OrderGuideGroupSelector: React.FC<GroupSelectorProps> = ({
  onCreateGroup,
  onSelectPartition,
  partitions,
  selectedPartitionUUID,
  validateGroupName,
}) => {
  const [styles] = useStyles();
  const scrollRef = useRef<HorizontalScrollWithArrowsHandle>(null);
  const isCreatingGroupRef = useRef(false);

  // Stable ref for the chip's editing-change effect dep.
  const handleNewGroupEditingChange = useCallback((isEditing: boolean) => {
    isCreatingGroupRef.current = isEditing;
  }, []);

  return (
    <HorizontalScrollWithArrows
      backAccessibilityLabel={i18n.t(
        'sales_portal.order_guides_list.groups.scroll_left',
      )}
      contentContainerStyle={styles.row}
      forwardAccessibilityLabel={i18n.t(
        'sales_portal.order_guides_list.groups.scroll_right',
      )}
      onContentSizeChange={() => {
        // Chase the New-group input as it expands at the end of the row
        if (isCreatingGroupRef.current) {
          scrollRef.current?.scrollToEnd();
        }
      }}
      ref={scrollRef}
      style={styles.scrollView}
    >
      {partitions.map((partition) => {
        const label =
          partition.name ??
          i18n.t('sales_portal.order_guides_list.groups.default');
        const selected = partition.partitionUUID === selectedPartitionUUID;
        return (
          <Chip
            accessibilityLabel={label}
            accessibilityState={{ selected }}
            circular
            key={partition.partitionUUID}
            onPress={() => onSelectPartition(partition.partitionUUID)}
            pressable
            selected={selected}
            size="$md"
          >
            <Chip.Text>{label}</Chip.Text>
          </Chip>
        );
      })}
      {onCreateGroup && validateGroupName ? (
        <OrderGuideNewGroupChip
          onCreateGroup={onCreateGroup}
          onEditingChange={handleNewGroupEditingChange}
          validateName={validateGroupName}
        />
      ) : null}
    </HorizontalScrollWithArrows>
  );
};

const useStyles = createStyles((theme) => ({
  row: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  scrollView: {
    flexGrow: 0,
  },
}));
